import Ajv2020, { type ValidateFunction } from "ajv/dist/2020.js";
import addFormats from "ajv-formats";
import {
	KMP_ARTIFACT_API_VERSION,
	KMP_ARTIFACT_CONTRACT_DIGEST,
	KMP_ARTIFACT_CONTRACT_VERSION,
	KMP_ARTIFACT_PACKAGE_NAME,
} from "@/contracts/kmpArtifactContract";
import canvasOperationContract from "../../../contracts/artist/operations/project.canvas.document.reduce.v1.json";
import reductionInputSchema from "../../../contracts/artist/schemas/project-canvas-document-reduce-input.schema.json";
import reductionResultSchema from "../../../contracts/artist/schemas/project-canvas-document-reduce-result.schema.json";
import documentStateSchema from "../../../contracts/artist/schemas/project-canvas-document-state.schema.json";

export const ARTIST_CANVAS_DOMAIN_PACKAGE = KMP_ARTIFACT_PACKAGE_NAME;
export const ARTIST_CANVAS_ARTIFACT_API_VERSION = KMP_ARTIFACT_API_VERSION;
export const ARTIST_CANVAS_CONTRACT_VERSION = KMP_ARTIFACT_CONTRACT_VERSION;
export const ARTIST_CANVAS_CONTRACT_DIGEST = KMP_ARTIFACT_CONTRACT_DIGEST;

export type ProjectCanvasAssetKind =
	| "text"
	| "lyrics"
	| "music"
	| "tts"
	| "image"
	| "video"
	| "asset_3d";

export interface ProjectCanvasTitle {
	readonly kind: "literal" | "localization-key";
	readonly value: string;
}

export interface ProjectCanvasPoint {
	readonly x: number;
	readonly y: number;
}

export interface ProjectCanvasSize {
	readonly width: number;
	readonly height: number;
}

export interface ProjectCanvasProject {
	readonly id: string;
	readonly kind: "project" | "music" | "video";
	readonly title: ProjectCanvasTitle;
	readonly ownerUserId: string | null;
	readonly releaseAlbumId: string | null;
	readonly releaseTrackId: string | null;
	readonly releaseVideoId: string | null;
	readonly createdAtEpochMilliseconds: number;
	readonly updatedAtEpochMilliseconds: number;
	readonly deletedAtEpochMilliseconds: number | null;
}

export interface ProjectCanvasNode {
	readonly id: string;
	readonly projectId: string;
	readonly assetKind: ProjectCanvasAssetKind;
	readonly title: ProjectCanvasTitle;
	readonly subtitle: string | null;
	readonly position: ProjectCanvasPoint;
	readonly size: ProjectCanvasSize;
	readonly zIndex: number;
	readonly mediaUrl: string | null;
	readonly thumbnailUrl: string | null;
	readonly widthPixels: number | null;
	readonly heightPixels: number | null;
	readonly durationMilliseconds: number | null;
	readonly format: string | null;
	readonly textContent: string | null;
	readonly createdAtEpochMilliseconds: number;
	readonly updatedAtEpochMilliseconds: number;
	readonly deletedAtEpochMilliseconds: number | null;
}

export interface ProjectCanvasEdge {
	readonly id: string;
	readonly projectId: string;
	readonly sourceNodeId: string;
	readonly targetNodeId: string;
	readonly relationship: "input" | "reference" | "generated";
	readonly createdAtEpochMilliseconds: number;
	readonly updatedAtEpochMilliseconds: number;
	readonly deletedAtEpochMilliseconds: number | null;
}

export interface ProjectCanvasGenerationRecord {
	readonly id: string;
	readonly projectId: string;
	readonly outputNodeId: string;
	readonly focusedNodeId: string | null;
	readonly referenceNodeIds: readonly string[];
	readonly lineageKey: string;
	readonly sourceJobTable: string | null;
	readonly sourceJobId: string | null;
	readonly provider: string | null;
	readonly modelKey: string | null;
	readonly modelDisplayName: string | null;
	readonly prompt: string | null;
	readonly negativePrompt: string | null;
	readonly mode: string | null;
	readonly configurationSummary: string | null;
	readonly creditsUsed: number | null;
	readonly idempotencyKey: string | null;
	readonly createdAtEpochMilliseconds: number;
}

export interface ProjectCanvasDocument {
	readonly project: ProjectCanvasProject;
	readonly nodes: readonly ProjectCanvasNode[];
	readonly edges: readonly ProjectCanvasEdge[];
	readonly generationRecords: readonly ProjectCanvasGenerationRecord[];
	readonly version: number;
	readonly updatedAtEpochMilliseconds: number;
}

export interface ProjectCanvasMoveOperation {
	readonly kind: "move-node";
	readonly projectId: string;
	readonly actorUserId: string;
	readonly idempotencyKey: string;
	readonly clientId: string;
	readonly clientSequence: number;
	readonly serverSequence: number;
	readonly nodeId: string;
	readonly position: ProjectCanvasPoint;
}

export interface ProjectCanvasCreateNodeOperation {
	readonly kind: "create-node";
	readonly projectId: string;
	readonly actorUserId: string;
	readonly idempotencyKey: string;
	readonly clientId: string;
	readonly clientSequence: number;
	readonly serverSequence: number;
	readonly nodeId: string;
	readonly assetKind: ProjectCanvasAssetKind;
	readonly position: ProjectCanvasPoint;
	readonly zIndex: number;
}

export interface ProjectCanvasCreateEdgeOperation {
	readonly kind: "create-edge";
	readonly projectId: string;
	readonly actorUserId: string;
	readonly idempotencyKey: string;
	readonly clientId: string;
	readonly clientSequence: number;
	readonly serverSequence: number;
	readonly sourceNodeId: string;
	readonly targetNodeId: string;
	readonly relationship: ProjectCanvasEdge["relationship"];
}

export interface ProjectCanvasGeneratedOutput {
	readonly nodeId: string;
	readonly assetKind: ProjectCanvasAssetKind;
	readonly title: ProjectCanvasTitle;
	readonly subtitle: string | null;
	readonly position: ProjectCanvasPoint;
	readonly size: ProjectCanvasSize;
	readonly zIndex: number;
	readonly mediaUrl: string | null;
	readonly thumbnailUrl: string | null;
	readonly widthPixels: number | null;
	readonly heightPixels: number | null;
	readonly durationMilliseconds: number | null;
	readonly format: string | null;
	readonly textContent: string | null;
	readonly generationId: string;
	readonly lineageKey: string;
	readonly sourceJobTable: string | null;
	readonly sourceJobId: string | null;
	readonly provider: string | null;
	readonly modelKey: string | null;
	readonly modelDisplayName: string | null;
	readonly prompt: string | null;
	readonly negativePrompt: string | null;
	readonly mode: string | null;
	readonly configurationSummary: string | null;
	readonly creditsUsed: number | null;
	readonly generationIdempotencyKey: string | null;
}

export interface ProjectCanvasCreateGeneratedNodesOperation {
	readonly kind: "create-generated-nodes";
	readonly projectId: string;
	readonly actorUserId: string;
	readonly idempotencyKey: string;
	readonly clientId: string;
	readonly clientSequence: number;
	readonly serverSequence: number;
	readonly focusedNodeId: string;
	readonly referenceNodeIds: readonly string[];
	readonly outputs: readonly ProjectCanvasGeneratedOutput[];
}

export interface ProjectCanvasDeleteNodeOperation {
	readonly kind: "delete-node";
	readonly projectId: string;
	readonly actorUserId: string;
	readonly idempotencyKey: string;
	readonly clientId: string;
	readonly clientSequence: number;
	readonly serverSequence: number;
	readonly nodeId: string;
}

export type ProjectCanvasOperation =
	| ProjectCanvasCreateNodeOperation
	| ProjectCanvasCreateEdgeOperation
	| ProjectCanvasCreateGeneratedNodesOperation
	| ProjectCanvasDeleteNodeOperation
	| ProjectCanvasMoveOperation;

export type ProjectCanvasOutcome =
	| {
			readonly kind: "success";
			readonly value: {
				readonly disposition:
					| "mutated"
					| "version-only"
					| "stale-noop"
					| "local-noop";
				readonly documentVersion: number;
			};
	  }
	| {
			readonly kind: "failure";
			readonly error: {
				readonly code: string;
				readonly category: string;
				readonly retryable: boolean;
				readonly messageKey: string;
				readonly context: Readonly<Record<string, JsonValue>>;
			};
	  };

export interface ProjectCanvasReductionResult {
	readonly outcome: ProjectCanvasOutcome;
	readonly finalState: ProjectCanvasDocument;
}

export interface ProjectCanvasKernelDescriptor {
	readonly packageName: typeof ARTIST_CANVAS_DOMAIN_PACKAGE;
	readonly artifactApiVersion: typeof ARTIST_CANVAS_ARTIFACT_API_VERSION;
	readonly contractVersion: typeof ARTIST_CANVAS_CONTRACT_VERSION;
	readonly contractDigest: typeof ARTIST_CANVAS_CONTRACT_DIGEST;
}

export interface ProjectCanvasKernel {
	readonly descriptor: Readonly<ProjectCanvasKernelDescriptor>;
	reduce(
		initialState: ProjectCanvasDocument,
		nowEpochMilliseconds: number,
		operation: ProjectCanvasOperation,
	): ProjectCanvasReductionResult;
}

export type ProjectCanvasKernelLoader = () => Promise<unknown>;

interface RawProjectCanvasKernelModule {
	describe(): unknown;
	projectCanvasDocumentReduce(requestJson: string): unknown;
}

const vendoredLoader: ProjectCanvasKernelLoader = () =>
	import("@xy-devmode/artist-domain");

const ajv = new Ajv2020({ allErrors: true, strict: true });
addFormats(ajv);
const validateDocument =
	ajv.compile<ProjectCanvasDocument>(documentStateSchema);
const validateReductionInput = ajv.compile(reductionInputSchema);
const validateReductionResult = ajv.compile(reductionResultSchema);
const validateFailureOutcome = ajv.compile({
	$schema: "https://json-schema.org/draft/2020-12/schema",
	$id: "https://artist.genart/contracts/schemas/project-canvas-document-failure-outcome.schema.json",
	type: "object",
	additionalProperties: false,
	required: ["kind", "error"],
	properties: {
		kind: { const: "failure" },
		error: {
			oneOf: canvasOperationContract.typedErrors.map((error) => ({
				type: "object",
				additionalProperties: false,
				required: ["code", "category", "retryable", "messageKey", "context"],
				properties: {
					code: { const: error.code },
					category: { const: error.category },
					retryable: { const: error.retryable },
					messageKey: { const: error.messageKey },
					context: { type: "object" },
				},
			})),
		},
	},
});

export class ProjectCanvasKernelUnavailableError extends Error {
	constructor(message: string, options?: ErrorOptions) {
		super(message, options);
		this.name = "ProjectCanvasKernelUnavailableError";
	}
}

export async function loadProjectCanvasKernel(
	loader: ProjectCanvasKernelLoader = vendoredLoader,
): Promise<ProjectCanvasKernel> {
	try {
		const module = extractModule(await loader());
		const descriptor = normalizeDescriptor(module.describe());
		return {
			descriptor,
			reduce(initialState, nowEpochMilliseconds, operation) {
				assertDocument(initialState);
				assertSchema(
					validateReductionInput,
					{ nowEpochMilliseconds, operation },
					"reduction input",
				);
				const requestJson = JSON.stringify({
					initialState,
					input: { nowEpochMilliseconds, operation },
				});
				const rawResult = module.projectCanvasDocumentReduce(requestJson);
				if (typeof rawResult !== "string") {
					throw new TypeError("Kotlin/JS reducer must return a JSON string");
				}
				return normalizeResult(JSON.parse(rawResult));
			},
		};
	} catch (error) {
		if (error instanceof ProjectCanvasKernelUnavailableError) throw error;
		throw new ProjectCanvasKernelUnavailableError(
			"The locked Kotlin/JS 0.8 Project Canvas reducer is unavailable or invalid",
			{ cause: error },
		);
	}
}

function extractModule(value: unknown): RawProjectCanvasKernelModule {
	const direct = moduleShape(value);
	if (direct !== null) return direct;
	if (isRecord(value)) {
		const nested = moduleShape(value.default);
		if (nested !== null) return nested;
	}
	throw new ProjectCanvasKernelUnavailableError(
		"The package does not expose projectCanvasDocumentReduce",
	);
}

function moduleShape(value: unknown): RawProjectCanvasKernelModule | null {
	if (!isRecord(value)) return null;
	if (
		typeof value.describe !== "function" ||
		typeof value.projectCanvasDocumentReduce !== "function"
	) {
		return null;
	}
	return value as unknown as RawProjectCanvasKernelModule;
}

function normalizeDescriptor(value: unknown): ProjectCanvasKernelDescriptor {
	if (!isRecord(value)) throw new TypeError("descriptor must be an object");
	assertExactKeys(value, [
		"packageName",
		"artifactApiVersion",
		"contractVersion",
		"contractDigest",
	]);
	for (const [field, expected] of [
		["packageName", ARTIST_CANVAS_DOMAIN_PACKAGE],
		["artifactApiVersion", ARTIST_CANVAS_ARTIFACT_API_VERSION],
		["contractVersion", ARTIST_CANVAS_CONTRACT_VERSION],
		["contractDigest", ARTIST_CANVAS_CONTRACT_DIGEST],
	] as const) {
		if (value[field] !== expected) {
			throw new ProjectCanvasKernelUnavailableError(
				`Kotlin/JS descriptor ${field} does not match the locked Web boundary`,
			);
		}
	}
	return Object.freeze(value as unknown as ProjectCanvasKernelDescriptor);
}

function normalizeResult(value: unknown): ProjectCanvasReductionResult {
	if (!isRecord(value))
		throw new TypeError("reduction result must be an object");
	assertExactKeys(value, ["outcome", "finalState"]);
	const outcome = normalizeOutcome(value.outcome);
	assertDocument(value.finalState);
	return { outcome, finalState: value.finalState };
}

function normalizeOutcome(value: unknown): ProjectCanvasOutcome {
	if (!isRecord(value)) throw new TypeError("outcome must be an object");
	if (value.kind === "success") {
		assertExactKeys(value, ["kind", "value"]);
		assertSchema(validateReductionResult, value.value, "success value");
		return value as unknown as ProjectCanvasOutcome;
	}
	if (value.kind === "failure") {
		assertSchema(validateFailureOutcome, value, "failure outcome");
		return value as unknown as ProjectCanvasOutcome;
	}
	throw new TypeError("outcome kind is invalid");
}

export function assertProjectCanvasDocument(
	value: unknown,
): asserts value is ProjectCanvasDocument {
	assertDocument(value);
}

function assertDocument(
	value: unknown,
): asserts value is ProjectCanvasDocument {
	assertSchema(validateDocument, value, "project canvas document");
}

function assertSchema(
	validator: ValidateFunction,
	value: unknown,
	label: string,
): void {
	if (!validator(value)) {
		throw new TypeError(
			`${label} violates the locked 0.8 schema: ${ajv.errorsText(validator.errors)}`,
		);
	}
}

function assertExactKeys(
	value: Readonly<Record<string, unknown>>,
	keys: readonly string[],
): void {
	const actual = Object.keys(value).toSorted();
	const expected = [...keys].toSorted();
	if (JSON.stringify(actual) !== JSON.stringify(expected)) {
		throw new TypeError(
			`object keys differ: expected ${expected.join(", ")}, received ${actual.join(", ")}`,
		);
	}
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === "object" && !Array.isArray(value);
}

type JsonPrimitive = null | boolean | number | string;
export type JsonValue =
	| JsonPrimitive
	| readonly JsonValue[]
	| { readonly [key: string]: JsonValue };
